import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SimpleModal from '../SimpleModal';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { bulkSetField } from '../../api/channelBulk';
import { fetchCategories, reorderCategories } from '../../api/categoriesApi';
import { getApiErrorMessage } from '../../api/http';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { usePlaylist } from '../../context/PlaylistContext';
import {
  CATEGORIES_REORDERED_EVENT,
  type CategoriesReorderedDetail,
} from '../../dashboard/categoryEditorEvents';

type CatDir = 'az' | 'za';
type ChannelSortField = 'name' | 'channel_number' | 'epg_id' | 'url';

function normCat(c: string | null | undefined): string {
  if (c == null || String(c).trim() === '') return 'UNCATEGORIZED';
  return String(c).trim().toUpperCase();
}

function naturalCmp(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function channelFieldCmp(a: ChannelRow, b: ChannelRow, field: ChannelSortField): number {
  if (field === 'channel_number') {
    const na = Number(a.channel_number ?? 0);
    const nb = Number(b.channel_number ?? 0);
    const fa = Number.isFinite(na) ? na : 0;
    const fb = Number.isFinite(nb) ? nb : 0;
    if (fa !== fb) return fa - fb;
    return naturalCmp(a.name || '', b.name || '');
  }
  const sa = String(a[field] ?? '');
  const sb = String(b[field] ?? '');
  const c = naturalCmp(sa, sb);
  if (c !== 0) return c;
  return naturalCmp(a.name || '', b.name || '');
}

export default function SortStreamsCategoriesModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { playlistId } = usePlaylist();
  const navigate = useNavigate();
  const [catDir, setCatDir] = useState<CatDir>('az');
  const [channelField, setChannelField] = useState<ChannelSortField>('name');
  const [busy, setBusy] = useState<'cats' | 'channels' | null>(null);

  useEffect(() => {
    if (!show) {
      setBusy(null);
      setCatDir('az');
      setChannelField('name');
    }
  }, [show]);

  const pid = Number(playlistId);

  const sortCategories = useCallback(async () => {
    if (!playlistId || !Number.isFinite(pid) || pid <= 0) {
      showToast('First select a playlist from Playlist manager.', 'warning');
      return;
    }
    setBusy('cats');
    try {
      const [dbCats, channels] = await Promise.all([
        fetchCategories(playlistId),
        fetchChannelsIndex(playlistId),
      ]);
      const names = new Set<string>();
      for (const c of dbCats) {
        const n = String(c.name || '').trim();
        if (n) names.add(n);
      }
      for (const ch of channels) {
        const n = String(ch.category || '').trim();
        if (!n) continue;
        const upper = n.toUpperCase();
        if (upper === 'UNCATEGORIZED' || upper === 'UNCLASSIFIED') continue;
        if (![...names].some((x) => x.toUpperCase() === upper)) names.add(n);
      }
      const order = [...names].sort((a, b) => naturalCmp(a, b));
      if (catDir === 'za') order.reverse();
      if (order.length === 0) {
        showToast('No categories to sort', 'info');
        return;
      }
      const res = await reorderCategories(pid, order);
      if (!res.success) {
        showToast(res.message || 'Failed to sort categories', 'error');
        return;
      }
      window.dispatchEvent(
        new CustomEvent<CategoriesReorderedDetail>(CATEGORIES_REORDERED_EVENT, {
          detail: { playlistId },
        }),
      );
      showToast(`Categories sorted ${catDir === 'az' ? 'A–Z' : 'Z–A'}`, 'success');
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to sort categories'), 'error');
    } finally {
      setBusy(null);
    }
  }, [catDir, pid, playlistId, refreshEditor, showToast]);

  const sortChannels = useCallback(async () => {
    if (!playlistId || !Number.isFinite(pid) || pid <= 0) {
      showToast('First select a playlist from Playlist manager.', 'warning');
      return;
    }
    setBusy('channels');
    try {
      const [dbCats, channels] = await Promise.all([
        fetchCategories(playlistId),
        fetchChannelsIndex(playlistId),
      ]);
      const catOrder: string[] = [];
      const seen = new Set<string>();
      for (const c of dbCats) {
        const k = normCat(c.name);
        if (!seen.has(k)) {
          seen.add(k);
          catOrder.push(k);
        }
      }
      for (const ch of channels) {
        const k = normCat(ch.category);
        if (!seen.has(k)) {
          seen.add(k);
          catOrder.push(k);
        }
      }

      const byCat = new Map<string, ChannelRow[]>();
      for (const ch of channels) {
        const k = normCat(ch.category);
        const list = byCat.get(k) ?? [];
        list.push(ch);
        byCat.set(k, list);
      }

      const ordered: ChannelRow[] = [];
      for (const cat of catOrder) {
        const list = byCat.get(cat) ?? [];
        list.sort((a, b) => channelFieldCmp(a, b, channelField));
        ordered.push(...list);
      }

      const updates: { id: number; value: number }[] = [];
      const undoById: Record<string, string | number | null> = {};
      const redoById: Record<string, string | number | null> = {};
      ordered.forEach((ch, i) => {
        const old = ch.sort_order ?? i;
        if (old !== i) {
          updates.push({ id: ch.id, value: i });
          undoById[String(ch.id)] = old;
          redoById[String(ch.id)] = i;
        }
      });

      if (updates.length === 0) {
        showToast('Channels are already in this order', 'info');
        return;
      }

      const res = await bulkSetField({ playlist_id: pid, field: 'sort_order', updates });
      if (!res.success) {
        showToast(res.message || 'Failed to sort channels', 'error');
        return;
      }
      record({
        label: 'Sort channels',
        undo: { type: 'setField', playlist_id: pid, field: 'sort_order', valuesById: undoById },
        redo: { type: 'setField', playlist_id: pid, field: 'sort_order', valuesById: redoById },
      });
      showToast(`Sorted ${updates.length} channels by ${channelField.replace('_', ' ')}`, 'success');
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to sort channels'), 'error');
    } finally {
      setBusy(null);
    }
  }, [channelField, pid, playlistId, record, refreshEditor, showToast]);

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Sort channels & categories"
      size="md"
      footer={
        <div className="d-flex justify-content-between gap-2 w-100 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              onClose();
              void navigate('/dashboard/channel-category');
            }}
          >
            Open category editor
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy != null}>
            Close
          </button>
        </div>
      }
    >
      <p className="text-muted small">
        Category order is saved for the playlist. Channel order is updated within each category (groups stay in place).
      </p>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title">Categories</h6>
          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="sortCatDir"
                id="sortCatAz"
                checked={catDir === 'az'}
                onChange={() => setCatDir('az')}
              />
              <label className="form-check-label" htmlFor="sortCatAz">
                A–Z (natural, case-insensitive)
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="sortCatDir"
                id="sortCatZa"
                checked={catDir === 'za'}
                onChange={() => setCatDir('za')}
              />
              <label className="form-check-label" htmlFor="sortCatZa">
                Z–A
              </label>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy != null}
            onClick={() => void sortCategories()}
          >
            {busy === 'cats' ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            Sort categories
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h6 className="card-title">Channels within each category</h6>
          <div className="mb-3">
            <label className="form-label" htmlFor="sortChannelField">
              Sort by
            </label>
            <select
              id="sortChannelField"
              className="form-select"
              value={channelField}
              onChange={(e) => setChannelField(e.target.value as ChannelSortField)}
            >
              <option value="name">Name</option>
              <option value="channel_number">Channel number</option>
              <option value="epg_id">EPG ID</option>
              <option value="url">URL</option>
            </select>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy != null}
            onClick={() => void sortChannels()}
          >
            {busy === 'channels' ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            Sort channels
          </button>
        </div>
      </div>
    </SimpleModal>
  );
}
